import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Button, Chips, StatusBadge, useToast } from '../components/ui';

interface ReportRow {
  id: string;
  entityType: string;
  entityId: string;
  reason: string;
  detail: string | null;
  status: string;
  createdAt: string;
  reporter: { id: string; mobile: string; displayName: string | null };
}

const LINK_FOR: Record<string, (id: string) => string> = {
  LISTING: (id) => `/listings?search=&highlight=${id}`,
  USER: (id) => `/users/${id}`,
  PROVIDER: () => `/providers`,
  CHAT_MESSAGE: () => `/chat`,
};

export function ReportsPage() {
  const { get, set } = useListParams({ status: 'OPEN' });
  const status = get('status');
  const toast = useToast();
  const qc = useQueryClient();

  const resolve = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIONED' | 'DISMISSED' }) =>
      api.patch(`/admin/reports/${id}`, { status }),
    onSuccess: (_d, v) => {
      toast(v.status === 'ACTIONED' ? 'Marked actioned' : 'Dismissed', 'success');
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['dashboard-badges'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<ReportRow>> = [
    {
      key: 'what',
      header: 'Reported',
      render: (r) => {
        const href = LINK_FOR[r.entityType]?.(r.entityId);
        return (
          <div>
            <div className="cell-strong">{humanise(r.entityType)}</div>
            {href ? (
              <Link className="cell-mono" to={href}>
                {r.entityId.slice(0, 8)}…
              </Link>
            ) : (
              <span className="cell-mono">{r.entityId.slice(0, 8)}…</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.reason}</div>
          {r.detail && (
            <div className="cell-muted cell-clip" title={r.detail}>
              {r.detail}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'reporter',
      header: 'Reported by',
      render: (r) => (
        <Link to={`/users/${r.reporter.id}`}>
          {r.reporter.displayName ?? fmtMobile(r.reporter.mobile)}
        </Link>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'when', header: 'Reported', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'OPEN' ? (
          <div className="cell-actions">
            <Button
              size="sm"
              variant="primary"
              loading={resolve.isPending && resolve.variables?.id === r.id}
              onClick={() => resolve.mutate({ id: r.id, status: 'ACTIONED' })}
            >
              Actioned
            </Button>
            <Button size="sm" onClick={() => resolve.mutate({ id: r.id, status: 'DISMISSED' })}>
              Dismiss
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <DataTable<ReportRow>
      queryKey={['reports', status]}
      path="/admin/reports"
      params={{ status }}
      columns={columns}
      emptyIcon="✅"
      emptyTitle={status === 'OPEN' ? 'No open reports' : 'Nothing with this status'}
      emptyBody={status === 'OPEN' ? 'Nothing has been reported that still needs a decision.' : undefined}
      toolbar={
        <Chips
          value={status}
          onChange={(v) => set('status', v)}
          options={[
            { value: 'OPEN', label: 'Open' },
            { value: 'ACTIONED', label: 'Actioned' },
            { value: 'DISMISSED', label: 'Dismissed' },
          ]}
        />
      }
    />
  );
}
